/* Phase Stripe-2 — /admin/coupons
 *
 * Admin CRUD for promo codes. Table + filters + New Coupon modal + expand
 * row to see redemption history.
 *
 * Backend contract:
 * - GET    /api/admin/coupons              → { items, total, ... }
 * - POST   /api/admin/coupons              (owner-only)
 * - GET    /api/admin/coupons/{id}         (includes redemption_history)
 * - PATCH  /api/admin/coupons/{id}         (owner-only, local + name only)
 * - DELETE /api/admin/coupons/{id}         (soft delete)
 */
import { useCallback, useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Percent, ToggleLeft, ToggleRight, Trash2, RefreshCw, X,
  ChevronDown, ChevronRight, BarChart3, Tag, Calendar, Users,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

import ConfirmDialog from "@/design-system/components/ConfirmDialog";

function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function StatusPill({ status }) {
  const colors = {
    active:      { bg: "rgba(134,239,172,0.16)", fg: "#86EFAC" },
    disabled:    { bg: "rgba(161,161,170,0.18)", fg: "#A1A1AA" },
    expired:     { bg: "var(--v2-danger-soft)", fg: "var(--v2-danger)" },
    exhausted:   { bg: "rgba(252,211,77,0.16)",  fg: "#FCD34D" },
  };
  const c = colors[status] || colors.disabled;
  return (
    <span
      data-testid={`admin-coupons-status-${status}`}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5"
      style={{ background: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
}

function discountLabel(c) {
  if (!c) return "—";
  if (c.discount_type === "percentage") return `${c.discount_value}% off`;
  return `$${(Number(c.discount_value) / 100).toFixed(2)} off`;
}

function durationLabel(c) {
  if (c.duration === "forever") return "Forever";
  if (c.duration === "once") return "One-time";
  return `Repeats × ${c.duration_in_months} mo`;
}

export default function CouponsAdmin() {
  const { has, isOwner } = useAdminPermissions();
  const canEdit = isOwner || has("edit_site_settings");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [details, setDetails] = useState({}); // id → detail payload
  const [confirmingRemove, setConfirmingRemove] = useState(null); // null | coupon
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (showActiveOnly) params.is_active = true;
      const { data } = await api.get("/admin/coupons", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly]);

  useEffect(() => { load(); }, [load]);

  async function toggleExpand(item) {
    const next = new Set(expanded);
    if (next.has(item.id)) {
      next.delete(item.id);
      setExpanded(next);
      return;
    }
    next.add(item.id);
    setExpanded(next);
    if (!details[item.id]) {
      try {
        const { data } = await api.get(`/admin/coupons/${item.id}`);
        setDetails((d) => ({ ...d, [item.id]: data }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  }

  async function toggleActive(item) {
    if (!canEdit) return;
    try {
      await api.patch(`/admin/coupons/${item.id}`, { is_active: !item.is_active });
      toast.success(`Coupon ${item.code} ${item.is_active ? "disabled" : "activated"}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function remove(item) {
    if (!canEdit) return;
    setRemoveBusy(true);
    try {
      await api.delete(`/admin/coupons/${item.id}`);
      toast.success(`Coupon ${item.code} disabled.`);
      setConfirmingRemove(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <AdminLayout
      title="Coupons"
      subtitle="Create, list, and manage promo codes powered by Stripe native Coupons + PromotionCodes."
      actions={
        <div className="flex items-center gap-2">
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Coupons"
          purpose="Manage promotional discount codes for the subscription plans."
          configures="Coupon code, discount percentage / amount, duration, redemption limit, expiration."
          impact="Live members can apply active coupons during Stripe checkout. Expired or disabled coupons are rejected at checkout."
          safety="Coupons can be soft-disabled at any time. Historical redemptions are preserved."
        />
      </div>
      
          <Link
            to="/admin/coupons/analytics"
            data-testid="admin-coupons-analytics-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--v2-border)] px-3 py-1.5 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
          >
            <BarChart3 className="w-3.5 h-3.5" /> Analytics
          </Link>
          <button
            data-testid="admin-coupons-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--v2-border)] px-3 py-1.5 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {canEdit && (
            <button
              data-testid="admin-coupons-create-btn"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wider"
              style={{ background: "#B794F4", color: "var(--v2-accent-ink)" }}
            >
              <Plus className="w-3.5 h-3.5" /> New coupon
            </button>
          )}
        </div>
      }
    >
      <div className="flex items-center gap-4 mb-5">
        <label className="flex items-center gap-2 text-xs text-[var(--v2-text-muted)]">
          <input
            type="checkbox"
            data-testid="admin-coupons-active-toggle"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
          Active only
        </label>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)]">
          {total} coupon{total === 1 ? "" : "s"}
        </span>
      </div>

      <div className="rounded-2xl border border-[var(--v2-border)] overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-coupons-table">
          <thead style={{ background: "var(--v2-surface)" }}>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)]">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Redemptions</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--v2-text-muted)]">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--v2-text-muted)]">
                No coupons yet. Create your first with the button above.
              </td></tr>
            )}
            {items.map((c) => {
              const isOpen = expanded.has(c.id);
              const detail = details[c.id];
              const usage = c.max_redemptions == null
                ? `${c.redemptions_count || 0} · ∞`
                : `${c.redemptions_count || 0} / ${c.max_redemptions}`;
              return (
                <Fragment key={c.id}>
                  <tr className="border-t border-[var(--v2-border)]" data-testid={`admin-coupons-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono text-[var(--v2-text)]">
                      <button
                        type="button"
                        data-testid={`admin-coupons-expand-${c.code}`}
                        onClick={() => toggleExpand(c)}
                        className="inline-flex items-center gap-2 text-[var(--v2-text)] hover:text-[#B794F4]"
                      >
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        <Tag className="w-3.5 h-3.5 text-[#B794F4]" />
                        {c.code}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[var(--v2-text-muted)]">{c.name}</td>
                    <td className="px-4 py-3 text-[var(--v2-text)]">{discountLabel(c)}</td>
                    <td className="px-4 py-3 text-[var(--v2-text-muted)]">{durationLabel(c)}</td>
                    <td className="px-4 py-3 text-[var(--v2-text-muted)]">
                      <span data-testid={`admin-coupons-redemptions-${c.code}`}>{usage}</span>
                      {c.remaining_redemptions !== null && c.remaining_redemptions !== undefined && (
                        <span className="ml-2 text-[10px] text-[var(--v2-text-muted)]">({c.remaining_redemptions} left)</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={c.current_status} /></td>
                    <td className="px-4 py-3 text-[var(--v2-text-muted)]">{fmtDate(c.expires_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && (
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`admin-coupons-toggle-${c.code}`}
                            onClick={() => toggleActive(c)}
                            className="text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
                            title={c.is_active ? "Disable" : "Enable"}
                          >
                            {c.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button
                            data-testid={`admin-coupons-delete-${c.code}`}
                            onClick={() => setConfirmingRemove(c)}
                            className="text-[var(--v2-text-muted)] hover:text-[var(--v2-danger)]"
                            title="Soft delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`admin-coupons-detail-row-${c.code}`} className="border-t border-[var(--v2-border)]">
                      <td colSpan={8} className="p-0" style={{ background: "var(--v2-surface-subtle)" }}>
                        <CouponDetailPanel coupon={c} detail={detail} canEdit={canEdit} onResynced={load} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateCouponDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmingRemove}
        onCancel={() => setConfirmingRemove(null)}
        onConfirm={() => confirmingRemove && remove(confirmingRemove)}
        busy={removeBusy}
        variant="destructive"
        title={confirmingRemove ? `Disable coupon ${confirmingRemove.code}?` : "Disable coupon"}
        description="This is a soft delete — existing redemptions are preserved for reporting, but the coupon can no longer be used for new checkouts."
        confirmLabel="Disable coupon"
        testId="coupons-delete-confirm"
      />
    </AdminLayout>
  );
}

function CouponDetailPanel({ coupon, detail, canEdit, onResynced }) {
  const [resyncing, setResyncing] = useState(false);
  async function resync() {
    if (resyncing) return;
    setResyncing(true);
    try {
      const { data } = await api.post(`/admin/coupons/${coupon._id}/resync-stripe`);
      if (data?.status === "already_synced") {
        toast.info("Coupon is already synced to Stripe.");
      } else {
        toast.success(data?.message || `Coupon ${coupon.code} synced to Stripe.`);
      }
      onResynced?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResyncing(false);
    }
  }
  if (!detail) {
    return (
      <div className="px-6 py-4 text-xs text-[var(--v2-text-muted)]">
        <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" /> Loading redemption history…
      </div>
    );
  }
  const history = detail.redemption_history || [];
  const syncedFully = coupon.stripe_coupon_id && coupon.stripe_promotion_code_id;
  return (
    <div className="px-6 py-5 space-y-4" data-testid={`admin-coupons-detail-${coupon.code}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px]">
        <div>
          <div className="text-[var(--v2-text-muted)] mb-1">Stripe Coupon ID</div>
          <div className="font-mono text-[var(--v2-text-muted)] break-all">
            {coupon.stripe_coupon_id || "— not synced —"}
          </div>
        </div>
        <div>
          <div className="text-[var(--v2-text-muted)] mb-1">Stripe Promotion Code ID</div>
          <div className="font-mono text-[var(--v2-text-muted)] break-all">
            {coupon.stripe_promotion_code_id || "— not synced —"}
          </div>
        </div>
        <div>
          <div className="text-[var(--v2-text-muted)] mb-1">Founder badge</div>
          <div className="text-[var(--v2-text-muted)]">{coupon.founder_badge_slug || "—"}</div>
        </div>
        <div>
          <div className="text-[var(--v2-text-muted)] mb-1">Applies to</div>
          <div className="text-[var(--v2-text-muted)]">
            {(coupon.applies_to_plans && coupon.applies_to_plans.length)
              ? coupon.applies_to_plans.join(", ")
              : "All paid plans"}
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid={`admin-coupons-resync-${coupon.code}`}
            onClick={resync}
            disabled={resyncing}
            title={syncedFully ? "Verify + refresh Stripe mirror" : "Create Stripe Coupon + PromotionCode from this local row"}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            style={{
              background: syncedFully ? "transparent" : "#B794F4",
              color: syncedFully ? "var(--v2-text)" : "#0A0A0F",
              border: syncedFully ? "1px solid var(--v2-border)" : "none",
            }}
          >
            {resyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncedFully ? "Verify Stripe sync" : "Sync to Stripe"}
          </button>
          {!syncedFully && (
            <span className="text-[10px] text-[#FCD34D] flex items-center gap-1">
              This coupon is not yet mirrored to Stripe — checkouts will fail until it is synced.
            </span>
          )}
        </div>
      )}
      {coupon.description && (
        <p className="text-[11px] text-[var(--v2-text-muted)] leading-relaxed">{coupon.description}</p>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--v2-text-muted)] mb-2">
          Redemption history ({history.length})
        </div>
        {history.length === 0 ? (
          <div className="text-[11px] text-[var(--v2-text-muted)]">No redemptions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid={`admin-coupons-redemptions-table-${coupon.code}`}>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[var(--v2-text-muted)]">
                  <th className="py-1.5 pr-3">Email</th>
                  <th className="py-1.5 pr-3">Subscription</th>
                  <th className="py-1.5 pr-3">Redeemed</th>
                  <th className="py-1.5 pr-3">Revenue impact</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r, i) => (
                  <tr key={i} className="border-t border-[var(--v2-border)]">
                    <td className="py-1.5 pr-3 text-[var(--v2-text-muted)]">{r.user_email || r.user_id}</td>
                    <td className="py-1.5 pr-3 font-mono text-[var(--v2-text-muted)]">{r.stripe_subscription_id || "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--v2-text-muted)]">{fmtDate(r.redeemed_at)}</td>
                    <td className="py-1.5 pr-3 text-[var(--v2-text)]">{fmtCents(r.revenue_impact_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Labeled({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[var(--v2-text-muted)] mt-1">{hint}</div>}
    </label>
  );
}

function CreateCouponDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    discount_type: "percentage",
    discount_value: "10",
    currency: "usd",
    duration: "once",
    duration_in_months: "",
    max_redemptions: "",
    expires_at: "",
    one_per_customer: true,
    stackable: false,
    applies_to_plans: "premium",
    beta_only: false,
    founder_badge_slug: "",
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        duration: form.duration,
        one_per_customer: !!form.one_per_customer,
        stackable: !!form.stackable,
        beta_only: !!form.beta_only,
        is_active: !!form.is_active,
      };
      if (form.discount_type === "fixed_amount") body.currency = form.currency;
      if (form.duration === "repeating") body.duration_in_months = Number(form.duration_in_months);
      if (form.max_redemptions) body.max_redemptions = Number(form.max_redemptions);
      if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
      if (form.founder_badge_slug.trim()) body.founder_badge_slug = form.founder_badge_slug.trim();
      const plans = form.applies_to_plans.split(",").map(s => s.trim()).filter(Boolean);
      if (plans.length) body.applies_to_plans = plans;
      await api.post("/admin/coupons", body);
      toast.success(`Coupon ${body.code} created and synced to Stripe.`);
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.fields) {
        toast.error(`Validation failed: ${detail.fields.map(f => `${f.field}: ${f.msg}`).join(" · ")}`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid="admin-coupons-create-dialog"
    >
      <div
        className="w-full max-w-2xl rounded-2xl border p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--v2-background)", borderColor: "var(--v2-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg text-[var(--v2-text)]">New coupon</h3>
          <button
            data-testid="admin-coupons-create-close"
            onClick={onClose}
            className="text-[var(--v2-text-muted)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Labeled label="Code (uppercase)" hint="3-64 chars · [A-Z0-9_-]">
            <input
              data-testid="admin-coupons-create-code"
              value={form.code}
              onChange={(e) => set("code", e.target.value.toUpperCase())}
              placeholder="LAUNCH25"
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm font-mono text-[var(--v2-text)]"
              required
            />
          </Labeled>
          <Labeled label="Display name">
            <input
              data-testid="admin-coupons-create-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Launch — 25% Off"
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
              required
            />
          </Labeled>
          <div className="md:col-span-2">
            <Labeled label="Description">
              <textarea
                data-testid="admin-coupons-create-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={2}
                className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
              />
            </Labeled>
          </div>
          <Labeled label="Discount type">
            <select
              data-testid="admin-coupons-create-discount-type"
              value={form.discount_type}
              onChange={(e) => set("discount_type", e.target.value)}
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            >
              <option value="percentage">Percentage</option>
              <option value="fixed_amount">Fixed amount (cents)</option>
            </select>
          </Labeled>
          <Labeled
            label={form.discount_type === "percentage" ? "Discount value (%)" : "Discount value (cents)"}
            hint={form.discount_type === "percentage" ? "0 < value ≤ 100" : "e.g. 500 = $5.00"}
          >
            <input
              data-testid="admin-coupons-create-discount-value"
              type="number"
              value={form.discount_value}
              onChange={(e) => set("discount_value", e.target.value)}
              min="1"
              max={form.discount_type === "percentage" ? "100" : undefined}
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
              required
            />
          </Labeled>
          {form.discount_type === "fixed_amount" && (
            <Labeled label="Currency">
              <input
                data-testid="admin-coupons-create-currency"
                value={form.currency}
                onChange={(e) => set("currency", e.target.value.toLowerCase())}
                placeholder="usd"
                className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
                required
              />
            </Labeled>
          )}
          <Labeled label="Duration">
            <select
              data-testid="admin-coupons-create-duration"
              value={form.duration}
              onChange={(e) => set("duration", e.target.value)}
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            >
              <option value="once">One-time (first invoice)</option>
              <option value="repeating">Repeats (choose months)</option>
              <option value="forever">Forever (life of subscription)</option>
            </select>
          </Labeled>
          {form.duration === "repeating" && (
            <Labeled label="Duration in months" hint="1-12">
              <input
                data-testid="admin-coupons-create-duration-in-months"
                type="number"
                min="1" max="12"
                value={form.duration_in_months}
                onChange={(e) => set("duration_in_months", e.target.value)}
                className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
                required
              />
            </Labeled>
          )}
          <Labeled label="Max redemptions (blank = ∞)">
            <input
              data-testid="admin-coupons-create-max-redemptions"
              type="number" min="1"
              value={form.max_redemptions}
              onChange={(e) => set("max_redemptions", e.target.value)}
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            />
          </Labeled>
          <Labeled label="Expires at (blank = never)">
            <input
              data-testid="admin-coupons-create-expires-at"
              type="date"
              value={form.expires_at}
              onChange={(e) => set("expires_at", e.target.value)}
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            />
          </Labeled>
          <Labeled label="Applies to plans" hint="Comma-separated plan codes. Blank = all paid plans.">
            <input
              data-testid="admin-coupons-create-plans"
              value={form.applies_to_plans}
              onChange={(e) => set("applies_to_plans", e.target.value)}
              placeholder="premium"
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            />
          </Labeled>
          <Labeled label="Founder badge slug (optional)" hint='e.g. "founding_member"'>
            <input
              data-testid="admin-coupons-create-founder-badge"
              value={form.founder_badge_slug}
              onChange={(e) => set("founder_badge_slug", e.target.value)}
              placeholder="founding_member"
              className="w-full bg-transparent border border-[var(--v2-border)] rounded-md px-3 py-2 text-sm text-[var(--v2-text)]"
            />
          </Labeled>
          <div className="md:col-span-2 flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 text-xs text-[var(--v2-text-muted)]">
              <input
                data-testid="admin-coupons-create-one-per-customer"
                type="checkbox"
                checked={form.one_per_customer}
                onChange={(e) => set("one_per_customer", e.target.checked)}
              />
              One use per customer
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--v2-text-muted)]">
              <input
                data-testid="admin-coupons-create-stackable"
                type="checkbox"
                checked={form.stackable}
                onChange={(e) => set("stackable", e.target.checked)}
              />
              Stackable
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--v2-text-muted)]">
              <input
                data-testid="admin-coupons-create-beta-only"
                type="checkbox"
                checked={form.beta_only}
                onChange={(e) => set("beta_only", e.target.checked)}
              />
              Beta only
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--v2-text-muted)]">
              <input
                data-testid="admin-coupons-create-is-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
              />
              Active on creation
            </label>
          </div>
          <div className="md:col-span-2 flex justify-end pt-2">
            <button
              type="submit"
              data-testid="admin-coupons-create-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-medium uppercase tracking-wider disabled:opacity-50"
              style={{ background: "#B794F4", color: "var(--v2-accent-ink)" }}
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create coupon
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
